import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { apiService } from "../../api/services/apiService";
import type { DeveloperGuide } from "../../types";
import { EntityStatus } from "../../types";
import DeveloperGuideSectionEditor from "../developer-guide/DeveloperGuideSectionEditor";
import DeveloperGuideRelationsPicker, {
  guideToRelationsSelection,
  relationsSelectionToGuideUuids,
  type DeveloperGuideRelationsSelection,
} from "../developer-guide/DeveloperGuideRelationsPicker";
import { EnhanceWithAiProvider } from "../developer-guide/EnhanceWithAiDrawer";
import { useDeveloperGuideAgentChat } from "../../hooks/useDeveloperGuideAgentChat";

interface EditDeveloperGuideDialogProps {
  guide: DeveloperGuide | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: DeveloperGuide) => void;
}

const EditDeveloperGuideDialog: React.FC<EditDeveloperGuideDialogProps> = ({
  guide,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<DeveloperGuide | null>(guide);
  const [relations, setRelations] = useState<DeveloperGuideRelationsSelection>({
    apis: [],
    products: [],
  });
  const [saving, setSaving] = useState(false);
  const { sendMessage, loading: aiLoading } = useDeveloperGuideAgentChat(
    guide?.tenantId ?? null,
  );

  useEffect(() => {
    setDraft(guide);
    if (guide) setRelations(guideToRelationsSelection(guide));
  }, [guide]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    setSaving(true);
    try {
      const updated = await apiService.updateDeveloperGuide(draft.uuid, {
        name: draft.name,
        description: draft.description ?? undefined,
        status: draft.status,
        sections: draft.sections,
        ...relationsSelectionToGuideUuids(relations),
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update developer guide");
    } finally {
      setSaving(false);
    }
  };

  const handleEnhanceOverview = async () => {
    if (!draft) return;
    try {
      const response = await sendMessage(
        `Improve the name and description for developer guide "${draft.name}". Description: "${draft.description ?? ""}". Return developer-guide-section JSON with title and instructions.`,
      );
      const titleMatch = response.match(/"title"\s*:\s*"([^"]+)"/);
      const descMatch = response.match(/"instructions"\s*:\s*"([^"]+)"/);
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              name: titleMatch ? titleMatch[1] : prev.name,
              description: descMatch
                ? descMatch[1].replace(/\\n/g, "\n")
                : prev.description,
            }
          : prev,
      );
    } catch {
      /* hook handles error */
    }
  };

  if (!draft) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Developer Guide</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <EnhanceWithAiProvider tenantId={draft.tenantId}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              startIcon={<AutoFixHighIcon />}
              disabled={aiLoading}
              onClick={handleEnhanceOverview}
            >
              Enhance overview
            </Button>
          </Box>
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            minRows={2}
            value={draft.description ?? ""}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
          />
          <TextField
            select
            fullWidth
            label="Status"
            margin="normal"
            value={draft.status}
            onChange={(e) =>
              setDraft({ ...draft, status: e.target.value as EntityStatus })
            }
          >
            {Object.values(EntityStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <DeveloperGuideRelationsPicker
            value={relations}
            onChange={setRelations}
          />

          <DeveloperGuideSectionEditor
            sections={draft.sections ?? []}
            onChange={(sections) => setDraft({ ...draft, sections })}
            guideName={draft.name}
            linkedApiUuids={relations.apis.map((a) => a.uuid)}
          />
        </EnhanceWithAiProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDeveloperGuideDialog;
